import { Breadcrumb, BreadcrumbIcon, BreadcrumbLink, BreadcrumbPage } from '@ads/components-react';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface ConsolidationTemplateProps {
  /** Nome do aluno — segmento intermediário do breadcrumb. */
  readonly studentName: string;
  readonly children: ReactNode;
}

/**
 * Layout da tela de consolidação: breadcrumb, cabeçalho com título e subtítulo,
 * e o slot de conteúdo (card do aluno, régua e tabela).
 *
 * A exportação em PDF vive no rodapé da
 * tabela, junto da seleção que define o que ela exporta — no topo, longe das
 * caixas de seleção, não daria para saber sobre o que ela agiria.
 *
 * Nenhuma ação escreve: a tela é um demonstrativo, e um menu "Mais ações" aqui
 * convidaria a operar a parcela no lugar errado.
 */
export function ConsolidationTemplate({ studentName, children }: ConsolidationTemplateProps) {
  const { t } = useTranslation('individualPayment');

  return (
    <div className="flex flex-col gap-xs">
      <div className="flex flex-col gap-nano">
        <Breadcrumb>
          <BreadcrumbLink href="/">
            <Link to="/">
              <BreadcrumbIcon />
            </Link>
          </BreadcrumbLink>
          <BreadcrumbLink href="/individual-payment">
            <Link to="/individual-payment">{t('breadcrumb')}</Link>
          </BreadcrumbLink>
          {/* O aluno é segmento intermediário: como `BreadcrumbPage` ele não
              emitiria o separador e a trilha terminaria sem dizer em que seção o
              operador está. `asChild={false}` mantém texto simples com o ">". */}
          <BreadcrumbLink asChild={false}>{studentName}</BreadcrumbLink>
          <BreadcrumbPage>{t('consolidation.title')}</BreadcrumbPage>
        </Breadcrumb>

        <div className="flex flex-wrap items-start justify-between gap-xs">
          <div className="flex flex-col gap-quark">
            <h1 className="ts-heading-lg text-ctx-content-title">{t('consolidation.title')}</h1>
            <p className="ts-paragraph-xs text-ctx-content-base">{t('consolidation.subtitle')}</p>
          </div>
        </div>
      </div>

      {children}
    </div>
  );
}
